import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Trophy, Users, Calendar, Crown, UserPlus, UserMinus, Play, Clock, Banknote, Gift, Pencil, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import TournamentForm, { tournamentToForm } from "@/components/TournamentForm";
import { TOURNAMENT_STATUS } from "@/pages/Tournaments";
import ComingSoon from "@/components/ComingSoon";
import { getDecimalLevel } from "@/lib/padelLevels";

export default function TournamentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [t, setT] = useState(null);
  const [loading, setLoading] = useState(true);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [resultDialog, setResultDialog] = useState(null);

  const fetchTournament = useCallback(() => {
    api.get(`/tournaments/${id}`).then(r => { setT(r.data); setLoading(false); }).catch(() => setLoading(false));
  }, [id]);
  useEffect(() => { fetchTournament(); }, [fetchTournament]);

  const isAdmin = user?.role === "admin";
  const myTeam = user && t?.teams?.find(tm => tm.player1_id === user.id || tm.partner_id === user.id);

  const call = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); fetchTournament(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (user?.role !== "admin") return <ComingSoon icon={Trophy} title="coming.tournaments" text="coming.tournaments_text" testId="tournaments-coming-soon" />;
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><div className="animate-spin h-8 w-8 border-2 border-coral rounded-full border-t-transparent" /></div>;
  if (!t) return <div className="text-center py-20 text-muted-foreground">Tournament not found</div>;

  const st = TOURNAMENT_STATUS[t.status] || TOURNAMENT_STATUS.open;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 py-8" data-testid="tournament-detail">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <h1 className="font-heading font-black text-2xl sm:text-3xl tracking-tight">{t.name}</h1>
              <Badge className={`rounded-full text-xs ${st.cls}`} data-testid="tournament-status">{st.label}</Badge>
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
              <span className="flex items-center gap-1"><Calendar size={14} /> {t.date}</span>
              <span className="flex items-center gap-1"><Clock size={14} /> {t.start_time}–{t.end_time}</span>
              <span className="flex items-center gap-1"><Banknote size={14} /> Rs {t.price_per_person} / person</span>
              <span className="flex items-center gap-1"><Users size={14} /> {t.teams?.length || 0}/{t.max_teams} teams</span>
              {t.prize && <span className="flex items-center gap-1 text-golden font-semibold"><Gift size={14} /> {t.prize}</span>}
            </div>
            {t.description && <p className="text-sm text-muted-foreground mt-3 max-w-2xl">{t.description}</p>}
          </div>
          <div className="flex gap-2 flex-wrap">
            {t.status === "open" && user && !myTeam && (
              <Button onClick={() => setRegisterOpen(true)} className="rounded-full bg-coral hover:bg-coral-600 text-white gap-1.5" data-testid="register-tournament-btn"><UserPlus size={14} /> Register my team</Button>
            )}
            {t.status === "open" && !user && (
              <Button onClick={() => navigate("/login")} className="rounded-full bg-coral hover:bg-coral-600 text-white gap-1.5" data-testid="login-to-register-btn"><UserPlus size={14} /> Sign in to register</Button>
            )}
            {t.status === "open" && myTeam && (
              <Button onClick={() => call(() => api.post(`/tournaments/${id}/unregister`), "Team withdrawn")} variant="outline" className="rounded-full gap-1.5 border-coral/30 text-coral" data-testid="unregister-tournament-btn"><UserMinus size={14} /> Withdraw</Button>
            )}
            {isAdmin && t.status === "open" && (
              <Button onClick={() => call(() => api.post(`/tournaments/${id}/confirm`), "Tournament confirmed — order of play published")} disabled={(t.teams?.length || 0) < 2}
                className="rounded-full bg-golden hover:bg-golden-500 text-midnight-500 gap-1.5 font-semibold" data-testid="confirm-tournament-btn"><Play size={14} /> Confirm & publish order of play</Button>
            )}
            {isAdmin && (
              <>
                <Button variant="ghost" size="icon" className="rounded-full" onClick={() => setEditOpen(true)} data-testid="edit-tournament-btn"><Pencil size={14} /></Button>
                <Button variant="ghost" size="icon" className="rounded-full text-destructive" data-testid="delete-tournament-btn"
                  onClick={() => { if (window.confirm("Delete this tournament?")) call(() => api.delete(`/tournaments/${id}`).then(() => navigate("/tournaments")), "Tournament deleted"); }}><Trash2 size={14} /></Button>
              </>
            )}
          </div>
        </div>

        {myTeam && (
          <div className="mb-6 p-4 rounded-2xl bg-teal/5 border border-teal/20 flex items-center justify-between gap-3 text-sm" data-testid="my-team-banner">
            <span>You're registered as <span className="font-semibold">{myTeam.name}</span> {myTeam.seed > 0 && <span className="text-muted-foreground">· seed #{myTeam.seed}</span>}</span>
            <Badge className={`rounded-full text-[10px] ${myTeam.payment_status === "paid" ? "bg-green-500/10 text-green-500" : "bg-golden/10 text-golden"}`}>
              {myTeam.payment_status === "paid" ? "Paid" : `Rs ${t.price_per_person * 2} due at the court / Juice`}
            </Badge>
          </div>
        )}

        {t.status === "completed" && t.champion && (
          <Card className="border-golden/30 bg-golden/5 mb-8" data-testid="champion-banner">
            <CardContent className="p-6 flex items-center gap-4 justify-center">
              <Crown size={28} className="text-golden" />
              <div className="text-center">
                <p className="text-xs text-golden uppercase tracking-wider font-semibold">Champions</p>
                <p className="font-heading font-black text-xl">{t.champion.name}</p>
              </div>
              <Crown size={28} className="text-golden" />
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="border-border/50 lg:col-span-1">
            <CardHeader className="pb-3"><CardTitle className="text-base">Teams ({t.teams?.length || 0}/{t.max_teams})</CardTitle></CardHeader>
            <CardContent className="space-y-2" data-testid="teams-list">
              {(t.teams || []).map((tm, i) => (
                <div key={tm.id} className="flex items-center justify-between gap-2 p-3 rounded-xl bg-muted/20 border border-border/30" data-testid="team-row">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{tm.seed > 0 && <span className="font-mono text-[10px] text-muted-foreground mr-1.5">#{tm.seed}</span>}{tm.name}</p>
                    <p className="text-[10px] text-muted-foreground">Lv {getDecimalLevel(tm.elo_rating)}{isAdmin && ` · ${tm.payment_status}`}</p>
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1 shrink-0">
                      <Button size="icon" variant="ghost" className={`w-7 h-7 rounded-full ${tm.payment_status === "paid" ? "text-green-500" : "text-muted-foreground"}`} title="Toggle paid"
                        onClick={() => call(() => api.put(`/tournaments/${id}/teams/${tm.id}/payment`, { paid: tm.payment_status !== "paid" }))} data-testid={`team-paid-toggle-${i}`}><Check size={13} /></Button>
                      {t.status === "open" && <Button size="icon" variant="ghost" className="w-7 h-7 rounded-full text-destructive" onClick={() => call(() => api.delete(`/tournaments/${id}/teams/${tm.id}`), "Team removed")} data-testid={`team-remove-${i}`}><X size={13} /></Button>}
                    </div>
                  )}
                </div>
              ))}
              {Array.from({ length: Math.max(0, t.max_teams - (t.teams?.length || 0)) }).map((_, i) => (
                <div key={`e-${i}`} className="h-10 rounded-xl border-2 border-dashed border-border/30 flex items-center justify-center text-[10px] text-muted-foreground/50">open spot</div>
              ))}
            </CardContent>
          </Card>

          <Card className="border-border/50 lg:col-span-2">
            <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Trophy size={16} className="text-golden" /> Order of play</CardTitle></CardHeader>
            <CardContent>
              {!t.bracket ? (
                <p className="text-sm text-muted-foreground py-10 text-center" data-testid="order-of-play-pending">The order of play is published once D&D Padel confirms the tournament.</p>
              ) : (
                <div className="space-y-6" data-testid="order-of-play">
                  {t.bracket.rounds.map((round) => (
                    <div key={round.round}>
                      <p className="text-[10px] text-muted-foreground uppercase tracking-wider font-semibold mb-2">{round.name}</p>
                      <div className="space-y-2">
                        {round.matches.filter(m => m.score !== "BYE").map(m => (
                          <MatchRow key={m.match_idx} m={m} isAdmin={isAdmin} onSubmit={() => setResultDialog(m)} />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <RegisterTeamDialog open={registerOpen} onClose={() => setRegisterOpen(false)} tournamentId={id} price={t.price_per_person} onDone={fetchTournament} />
        <Dialog open={editOpen} onOpenChange={setEditOpen}>
          <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit tournament</DialogTitle><DialogDescription>Update the event details</DialogDescription></DialogHeader>
            <TournamentForm initial={tournamentToForm(t)} tournamentId={id} onSaved={() => { setEditOpen(false); fetchTournament(); }} />
          </DialogContent>
        </Dialog>
        {resultDialog && <ResultDialog match={resultDialog} onClose={() => setResultDialog(null)}
          onSubmit={(winnerId, score) => call(() => api.post(`/tournaments/${id}/matches/${resultDialog.match_idx}/result`, { winner_id: winnerId, score }).then(() => setResultDialog(null)), "Result saved")} />}
      </motion.div>
    </div>
  );
}

function MatchRow({ m, isAdmin, onSubmit }) {
  const canSubmit = isAdmin && m.player1 && m.player2 && !m.winner;
  const Team = ({ p }) => p ? (
    <span className={`text-sm truncate ${m.winner?.id === p.id ? "font-bold text-teal" : "font-medium"}`}>{p.name}{m.winner?.id === p.id && <Trophy size={10} className="inline ml-1 text-golden" />}</span>
  ) : <span className="text-xs italic text-muted-foreground">TBD</span>;
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl border ${m.winner ? "border-border/30 bg-muted/10" : canSubmit ? "border-coral/40" : "border-border/30"}`} data-testid={`order-match-${m.match_idx}`}>
      <div className="w-14 shrink-0 text-center"><p className="font-mono font-bold text-sm">{m.time || "—"}</p><p className="text-[9px] text-muted-foreground uppercase">Central Court</p></div>
      <div className="flex-1 grid grid-cols-[1fr_auto_1fr] items-center gap-2 min-w-0">
        <div className="text-right truncate"><Team p={m.player1} /></div>
        <span className="text-[10px] text-muted-foreground font-semibold">{m.score && m.winner ? m.score : "vs"}</span>
        <div className="truncate"><Team p={m.player2} /></div>
      </div>
      {canSubmit && <Button size="sm" variant="outline" className="rounded-full text-xs border-coral/30 text-coral h-7" onClick={onSubmit} data-testid={`submit-result-${m.match_idx}`}>Result</Button>}
    </div>
  );
}

function RegisterTeamDialog({ open, onClose, tournamentId, price, onDone }) {
  const [partnerName, setPartnerName] = useState("");
  const [partnerEmail, setPartnerEmail] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/tournaments/${tournamentId}/register`, { partner_name: partnerName, partner_email: partnerEmail });
      toast.success("Team registered! Pay at the court or via MCB Juice.");
      onClose(); onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>Register your team</DialogTitle><DialogDescription>You + one partner. Rs {price} per person, payable at the court or via MCB Juice.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label>Partner's name</Label><Input value={partnerName} onChange={e => setPartnerName(e.target.value)} placeholder="e.g. Kevin" className="rounded-xl" data-testid="partner-name-input" /></div>
          <div><Label>Partner's email <span className="text-[10px] text-muted-foreground font-normal">(optional, if they have an account)</span></Label><Input type="email" value={partnerEmail} onChange={e => setPartnerEmail(e.target.value)} className="rounded-xl" data-testid="partner-email-input" /></div>
          <Button onClick={submit} disabled={saving || !partnerName.trim()} className="w-full rounded-full bg-coral hover:bg-coral-600 text-white" data-testid="register-team-submit">{saving ? "Registering..." : "Register team"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ResultDialog({ match, onSubmit, onClose }) {
  const [score, setScore] = useState("");
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-xs">
        <DialogHeader><DialogTitle className="text-base">Who won?</DialogTitle><DialogDescription>Select the winning team</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Score (e.g. 6-3, 7-5)" value={score} onChange={e => setScore(e.target.value)} className="rounded-xl text-center" data-testid="result-score-input" />
          {[match.player1, match.player2].filter(Boolean).map(p => (
            <button key={p.id} onClick={() => onSubmit(p.id, score)} className="w-full flex items-center gap-3 p-3 rounded-xl border border-border/30 hover:border-coral/40 hover:bg-coral/5 transition-all" data-testid={`pick-winner-${p.id}`}>
              <span className="font-semibold text-sm flex-1 text-left">{p.name}</span><Crown size={14} className="text-golden/40" />
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
